import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Bell,
  CheckCheck,
  ThumbsUp,
  ThumbsDown,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import {
  fetchPortalNotifications,
  markPortalNotificationRead,
  markAllPortalNotificationsRead,
} from '@/lib/queries';
import {
  PORTAL_NOTIFICATION_EVENT,
  usePortalRealtime,
} from '@/contexts/PortalRealtimeContext';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, timeAgo } from '@/lib/utils';
import type { PortalNotification } from '@/types/portal';

const TYPE_ICONS: Record<string, LucideIcon> = {
  invite_accepted: ThumbsUp,
  invite_declined: ThumbsDown,
  team_member_joined: UserPlus,
};

export default function Notifications() {
  const navigate = useNavigate();
  const { refreshUnread } = usePortalRealtime();
  const [notifications, setNotifications] = useState<PortalNotification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPortalNotifications()
      .then(setNotifications)
      .catch(() => toast.error('Failed to load notifications'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onNew = (event: Event) => {
      const notification = (event as CustomEvent<PortalNotification>).detail;
      setNotifications(previous => {
        if (previous.some(item => item.id === notification.id)) return previous;
        return [notification, ...previous];
      });
    };
    window.addEventListener(PORTAL_NOTIFICATION_EVENT, onNew);
    return () => window.removeEventListener(PORTAL_NOTIFICATION_EVENT, onNew);
  }, []);

  const handleOpen = async (notification: PortalNotification) => {
    if (!notification.read_at) {
      try {
        await markPortalNotificationRead(notification.id);
        setNotifications(previous =>
          previous.map(item =>
            item.id === notification.id
              ? { ...item, read_at: new Date().toISOString() }
              : item
          )
        );
        refreshUnread();
      } catch {
        // non-blocking
      }
    }
    if (notification.link) navigate(notification.link);
  };

  const handleMarkAll = async () => {
    try {
      await markAllPortalNotificationsRead();
      setNotifications(previous =>
        previous.map(item => ({
          ...item,
          read_at: item.read_at ?? new Date().toISOString(),
        }))
      );
      refreshUnread();
    } catch {
      toast.error('Failed to mark all read');
    }
  };

  const hasUnread = notifications.some(notification => !notification.read_at);

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">Notifications</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Team invites and studio activity for your publisher account.
          </p>
        </div>
        {hasUnread && (
          <Button variant="outline" size="sm" onClick={handleMarkAll}>
            <CheckCheck className="size-4" /> Mark all read
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-white/10 px-6 py-16 text-center">
          <Bell className="mb-3 size-10 text-muted-foreground/50" />
          <p className="font-medium">Nothing yet</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            You&apos;ll see team invite responses and studio updates here.
          </p>
        </div>
      ) : (
        <div className="flex flex-col overflow-hidden rounded-lg border border-white/[0.08]">
          {notifications.map(notification => {
            const Icon = TYPE_ICONS[notification.type] ?? Bell;
            const unread = !notification.read_at;
            return (
              <button
                key={notification.id}
                type="button"
                onClick={() => handleOpen(notification)}
                className={cn(
                  'flex items-start gap-3 border-b border-white/[0.06] p-4 text-left transition-colors last:border-b-0 hover:bg-white/[0.03]',
                  unread && 'bg-white/[0.02]'
                )}
              >
                <div
                  className={cn(
                    'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md',
                    unread
                      ? 'bg-primary/15 text-primary'
                      : 'bg-muted text-muted-foreground'
                  )}
                >
                  <Icon className="size-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className={cn('text-sm', unread ? 'font-semibold' : 'font-medium')}>
                      {notification.title}
                    </p>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {timeAgo(notification.created_at)}
                    </span>
                  </div>
                  {notification.body && (
                    <p className="mt-0.5 text-sm text-muted-foreground">{notification.body}</p>
                  )}
                </div>
                {unread && (
                  <span className="mt-2 size-2 shrink-0 rounded-full bg-primary" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
